import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, XCircle, Warning, ShieldCheck, Copy } from "@phosphor-icons/react";

/**
 * Deploy Health — one-click red/green check across every /api/* route.
 *
 * "Internal" column pings routes from *within* the FastAPI process
 * (via localhost via the backend's own httpx client) — proves the code
 * works.
 *
 * "Public" column re-runs the same checks over the browser-visible URL
 * (defaults to REACT_APP_BACKEND_URL).  If Internal is green but Public
 * is red, the problem is 100% Nginx/Cloudflare/CDN — NOT code.  This is
 * the single most common cause of the "why is my prod site broken" call.
 */
export default function DeployPanel({ token }) {
  const [rows, setRows] = useState(null);
  const [publicRows, setPublicRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publicBase, setPublicBase] = useState(() => {
    try { return process.env.REACT_APP_BACKEND_URL || window.location.origin; }
    catch { return ""; }
  });
  const [alsoPublic, setAlsoPublic] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const params = alsoPublic && publicBase ? { public_base: publicBase } : {};
      const { data } = await axios.get(`${API}/admin/deploy/health`, {
        headers: { "X-Admin-Token": token },
        params,
      });
      setRows(data.internal || []);
      setPublicRows(data.public || []);
      setCounts(data.counts);
      setLastRunAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health probe failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyReport = () => {
    if (!rows) return;
    const lines = ["# Wisewit deploy health", `Ran: ${lastRunAt?.toISOString() || ""}`, ""];
    lines.push("## Internal (from FastAPI process)");
    rows.forEach((r) => lines.push(`${r.ok ? "OK " : "FAIL"} ${String(r.status).padStart(3)}  ${r.ms}ms  ${r.method} ${r.path}${r.error ? "  " + r.error : ""}`));
    if (publicRows.length) {
      lines.push("", "## Public (via browser URL)");
      publicRows.forEach((r) => lines.push(`${r.ok ? "OK " : "FAIL"} ${String(r.status).padStart(3)}  ${r.ms}ms  ${r.method} ${r.path}${r.error ? "  " + r.error : ""}`));
    }
    navigator.clipboard.writeText(lines.join("\n")).then(
      () => toast.success("Report copied to clipboard"),
      () => toast.error("Copy failed"),
    );
  };

  const publicByPath = Object.fromEntries((publicRows || []).map((r) => [r.path, r]));

  return (
    <div data-testid="deploy-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-overline mb-2">◢ Deploy health</div>
          <div className="text-[#8E8E93] text-sm max-w-2xl">
            Red = broken. Green = healthy. If <b className="text-white">Internal is green but Public is red</b>, the problem is your Nginx or Cloudflare config, not the code.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={run} disabled={loading} className="btn-primary" data-testid="deploy-run">
            <ArrowsClockwise size={14} /> {loading ? "Probing…" : "Run checks"}
          </button>
          <button onClick={copyReport} disabled={!rows} className="btn-ghost" data-testid="deploy-copy">
            <Copy size={14} /> Copy report
          </button>
        </div>
      </div>

      {/* Public URL comparison toggle */}
      <div className="border border-[#2A2A2A] p-4 mb-6 bg-[#0a0a0a]">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={alsoPublic}
            onChange={(e) => setAlsoPublic(e.target.checked)}
            data-testid="deploy-public-toggle"
            className="mt-1"
          />
          <div className="flex-1">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">Also probe public URL (compare-to-Nginx)</div>
            <div className="mt-1 text-[#8E8E93] text-sm">Runs the same checks against your browser-visible URL. Reveals CDN/Nginx routing failures instantly.</div>
            <input
              value={publicBase}
              onChange={(e) => setPublicBase(e.target.value)}
              placeholder="https://wisewit.ai"
              disabled={!alsoPublic}
              className="ww-input mt-2 font-mono text-xs max-w-md disabled:opacity-40"
              data-testid="deploy-public-base"
            />
          </div>
        </label>
      </div>

      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="deploy-counts">
          <StatCard label="Internal pass" value={`${counts.internal_pass}/${counts.internal_total}`} color={counts.internal_fail === 0 ? "#00FFB2" : "#FF4500"} />
          <StatCard label="Internal fail" value={counts.internal_fail} color={counts.internal_fail === 0 ? "#8E8E93" : "#FF3060"} />
          <StatCard label="Public pass" value={counts.public_total > 0 ? `${counts.public_pass}/${counts.public_total}` : "—"} color={counts.public_total === 0 ? "#8E8E93" : (counts.public_fail === 0 ? "#00FFB2" : "#FF4500")} />
          <StatCard label="Public fail" value={counts.public_total > 0 ? counts.public_fail : "—"} color={counts.public_total === 0 ? "#8E8E93" : (counts.public_fail === 0 ? "#8E8E93" : "#FF3060")} />
        </div>
      )}

      {/* Diagnostic banner */}
      {counts && counts.public_total > 0 && counts.internal_fail === 0 && counts.public_fail > 0 && (
        <div className="border border-[#FF4500] bg-[#FF4500]/[0.08] p-4 mb-6 flex items-start gap-3" data-testid="deploy-diag-proxy">
          <Warning size={22} color="#FF4500" weight="fill" />
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF4500]">Reverse-proxy issue detected</div>
            <div className="mt-1 text-[#F5F5F5] text-sm">
              Internal is 100% green but the public URL fails on some routes. This is your Nginx <code className="text-[#DFFF00]">/api/</code> config or Cloudflare CDN — NOT the code.
              Common fixes: bump <code className="text-[#DFFF00]">client_max_body_size 500m</code>, purge Cloudflare cache, verify proxy_pass port matches backend port.
            </div>
          </div>
        </div>
      )}
      {counts && counts.public_total > 0 && counts.internal_fail === 0 && counts.public_fail === 0 && (
        <div className="border border-[#00FFB2] bg-[#00FFB2]/[0.06] p-4 mb-6 flex items-start gap-3" data-testid="deploy-diag-ok">
          <ShieldCheck size={22} color="#00FFB2" weight="fill" />
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#00FFB2]">All systems green</div>
            <div className="mt-1 text-[#F5F5F5] text-sm">Internal and public checks all pass. Your deploy is healthy end-to-end.</div>
          </div>
        </div>
      )}

      {rows && (
        <div className="border border-[#2A2A2A]" data-testid="deploy-rows">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
            <div className="col-span-4">Route</div>
            <div className="col-span-1">Method</div>
            <div className={alsoPublic ? "col-span-3" : "col-span-5"}>Internal</div>
            {alsoPublic && <div className="col-span-3">Public</div>}
            <div className="col-span-1 text-right">Guarded</div>
          </div>
          {rows.map((r) => {
            const pub = publicByPath[r.path];
            return (
              <div key={r.path} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] text-sm items-center hover:bg-[#0a0a0a]">
                <div className="col-span-4">
                  <div className="text-white">{r.name}</div>
                  <div className="font-mono text-[10px] text-[#8E8E93] truncate" title={r.path}>{r.path}</div>
                </div>
                <div className="col-span-1 font-mono text-[10px] text-[#DFFF00]">{r.method}</div>
                <StatusPill row={r} span={alsoPublic ? 3 : 5} testid={`deploy-int-${r.path}`} />
                {alsoPublic && <StatusPill row={pub || {status: 0, ok: false, ms: 0, error: 'not run'}} span={3} testid={`deploy-pub-${r.path}`} muted={!pub} />}
                <div className="col-span-1 text-right font-mono text-[10px]" style={{ color: r.requires_admin ? "#FF4500" : "#8E8E93" }}>
                  {r.requires_admin ? "admin" : "public"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="border border-[#2A2A2A] p-4 bg-[#0a0a0a]">
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">{label}</div>
      <div className="mt-2 text-2xl font-display" style={{ color }}>{value}</div>
    </div>
  );
}

function StatusPill({ row, span, testid, muted }) {
  const ok = row?.ok;
  const status = row?.status ?? 0;
  const ms = row?.ms ?? 0;
  const bg = muted ? "#1A1A1A" : (ok ? "rgba(0,255,178,0.08)" : "rgba(255,48,96,0.08)");
  const color = muted ? "#555" : (ok ? "#00FFB2" : "#FF3060");
  const Icon = ok ? CheckCircle : XCircle;
  return (
    <div className={`col-span-${span} flex items-center gap-2 min-w-0`} data-testid={testid}>
      <div className="flex items-center gap-2 px-2 py-1 font-mono text-[11px]" style={{ background: bg, color }}>
        <Icon size={12} weight="fill" />
        <span>{status || "—"}</span>
        <span className="text-[#8E8E93]">·</span>
        <span className="text-[#8E8E93]">{ms}ms</span>
      </div>
      {row?.error && !muted && (
        <div className="font-mono text-[10px] text-[#FF3060] truncate flex-1" title={row.error}>{row.error}</div>
      )}
    </div>
  );
}
